import { useScrollAnimation } from '../../hooks/useScrollAnimation';

const regions = [
  {
    label: 'South-Central',
    note: 'Oklahoma · Texas',
    detail: 'Oklahoma City, Tulsa, Houston, Midland, Odessa, Corpus Christi',
    icon: 'ri-map-pin-2-fill',
    primary: true,
  },
  {
    label: 'Southwest & Mountain',
    note: 'Arizona · New Mexico · Colorado',
    detail: 'Phoenix, Albuquerque, Hobbs, Carlsbad, Denver, Bakersfield',
    icon: 'ri-map-pin-2-fill',
    primary: false,
  },
  {
    label: 'Southeast & Gulf',
    note: 'Georgia · Florida · Louisiana',
    detail: 'Atlanta, Tampa, Jacksonville, New Orleans, Nashville',
    icon: 'ri-map-pin-2-fill',
    primary: false,
  },
  {
    label: 'Midwest & Northeast',
    note: 'Illinois · Pennsylvania · Ohio',
    detail: 'Chicago, Pittsburgh, Kansas City, Indianapolis, Cleveland',
    icon: 'ri-map-pin-2-fill',
    primary: false,
  },
  {
    label: 'Northwest & Alaska',
    note: 'Washington · Oregon · Alaska',
    detail: 'Seattle, Portland, Anchorage, Boise, Salt Lake City',
    icon: 'ri-map-pin-2-fill',
    primary: false,
  },
  {
    label: 'International',
    note: 'Canada · UK · Australia · UAE',
    detail: 'Calgary, London, Melbourne, Dubai, Amsterdam, Wellington',
    icon: 'ri-earth-fill',
    primary: false,
  },
];

export default function ServiceAreas() {
  const { ref, isVisible } = useScrollAnimation<HTMLDivElement>();
  const { ref: gridRef, isVisible: gridVisible } = useScrollAnimation<HTMLDivElement>();

  return (
    <section className="bg-[#0A0A0A] py-24 px-6 lg:px-14">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-14">
          <div>
            <p className="overline mb-4">Coverage Area</p>
            <h2 className="font-serif text-4xl lg:text-5xl font-light text-[#F2EDE3] leading-tight gold-line">
              We Serve Contractors Anywhere
            </h2>
          </div>
          <div className="flex items-end">
            <p className="text-[#9A9A9A] text-sm font-light leading-relaxed lg:pb-2">
              Energy Contractor Services provides ISNetworld, Veriforce, and Avetta compliance management across all 50 US states and internationally. Compliance management is handled remotely — wherever you work, we can help.
            </p>
          </div>
        </div>

        {/* Map */}
        <div
          ref={ref}
          className={`w-full mb-12 fade-up ${isVisible ? 'visible' : ''}`}
        >
          <div className="relative border border-[#C9A030]/12 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#0A0A0A]/40 pointer-events-none z-10" />
            <iframe
              title="Energy Contractor Services — Oklahoma City"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d412659.7937673745!2d-97.67440855!3d35.46765265!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x87ad8a547ef4dbc1%3A0x33e10d5ae828a01d!2sOklahoma+City%2C+OK!5e0!3m2!1sen!2sus!4v1710000000000"
              width="100%"
              height="320"
              style={{ border: 0, display: 'block', filter: 'grayscale(100%) contrast(1.05) brightness(0.45) sepia(20%)' }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
            {/* Map overlay badge */}
            <div className="absolute bottom-4 left-4 z-20 bg-[#0A0A0A]/90 border border-[#C9A030]/25 px-5 py-3 flex items-center gap-3 backdrop-blur-sm">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-map-pin-fill text-[#C9A030] text-sm" />
              </div>
              <div>
                <p className="text-[#F2EDE3] text-xs font-semibold tracking-wide">Oklahoma City, OK</p>
                <p className="text-[#9A9A9A] text-[10px] tracking-wide font-light">Serving contractors worldwide</p>
              </div>
            </div>
          </div>
        </div>

        {/* Region Grid */}
        <div
          ref={gridRef}
          className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#F2EDE3]/04 fade-up ${gridVisible ? 'visible' : ''}`}
        >
          {regions.map((region, idx) => (
            <div
              key={idx}
              className={`p-7 transition-colors duration-300 group ${region.primary ? 'bg-[#111111] border-t-2 border-[#C9A030]' : 'bg-[#0A0A0A] hover:bg-[#0E0E0E]'}`}
              style={{ transitionDelay: `${idx * 0.07}s` }}
            >
              <div className="flex items-start gap-3 mb-3">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className={`${region.icon} text-[#C9A030] text-sm`} />
                </div>
                <div>
                  <p className={`text-sm font-medium tracking-wide mb-1 ${region.primary ? 'text-[#C9A030]' : 'text-[#F2EDE3]'}`}>
                    {region.label}
                  </p>
                  <p className="text-[#C9A030]/70 text-[10px] tracking-[0.18em] uppercase font-medium">
                    {region.note}
                  </p>
                </div>
              </div>
              <p className="text-[#9A9A9A] text-xs font-light leading-relaxed ml-8">
                {region.detail}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-8 flex items-center justify-center gap-3">
          <div className="w-8 h-px bg-[#C9A030]/30" />
          <p className="text-[#5A5A5A] text-xs font-light tracking-wide">
            Compliance management is handled remotely — location is never a barrier
          </p>
          <div className="w-8 h-px bg-[#C9A030]/30" />
        </div>
      </div>
    </section>
  );
}
